import React, { useEffect, useState } from 'react';
import { Box, Grid, Drawer, IconButton, Typography, CircularProgress, Stack } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Images from '../utils/Images';
import toast from 'react-hot-toast';
import { ADDON_SIZE_HEIGHTS, getAddonSizeTier, isSizeAddonGroup } from './AddonIconComponent';

const ProductDrawer = ({
  open,
  onClose,
  selectedProduct,
  handleAddToCart,
  quantity,
  handleIncreaseQuantity,
  handleDecreaseQuantity,
  selectedAddons,
  handleAddonSelect,
  totalPrice,
  totalMrpPrice,
  offerPercentage,
  addingToCart,
  loadingProductDetails = false,
}) => {

  const [outletData, setOutletData] = useState(JSON.parse(localStorage.getItem('selectedStoreData')));
  const [isMultiple, setIsMultiple] = useState(true);

  useEffect(() => {
    if (!selectedProduct?.addons) return;

    selectedProduct?.addons?.forEach(addon => {
      // skip addons with no defaults
      const defaults = addon?.values
        .filter(v => v?.isDefault
          && v?.status !== 'INACTIVE'
          && !v?.excludedOutlets?.includes(outletData?._id));

      if (defaults?.length) {
        if (addon?.isMultipleSelect) {
          // multiple defaults → pass an array
          handleAddonSelect(
            addon._id,
            defaults.map(v => ({
              ...v,
              offerPrice: v?.price ?? 0,
              mrp: v?.price ?? 0,
            })),
            true
          );
        } else {
          const v = defaults[0];
          handleAddonSelect(
            addon._id,
            { ...v, offerPrice: v?.price ?? 0, mrp: v?.price ?? 0 },
            false
          );
        }
      }
    });
  }, [selectedProduct, outletData]);

  const validateAddonsBeforeAddToCart = () => {
    const invalidAddonTitles = [];

    selectedProduct?.addons?.forEach((addon) => {
      const hasDefaultExcluded = addon?.values?.some(
        (val) => val?.isDefault && val?.excludedOutlets?.includes(outletData?._id)
      );

      const hasDefaultInactive = addon?.values?.some(
        (val) => val?.isDefault && val?.status === 'INACTIVE'
      );

      const selected = selectedAddons[addon?._id];

      let isInvalid = false;

      if (addon?.isMultipleSelect) {
        isInvalid =
          (hasDefaultExcluded || hasDefaultInactive) &&
          (!Array.isArray(selected) || selected.length === 0);
      } else {
        isInvalid =
          (hasDefaultExcluded || hasDefaultInactive) &&
          (!selected ||
            selected?.excludedOutlets?.includes(outletData?._id) ||
            selected?.status === 'INACTIVE');
      }

      if (isInvalid) {
        invalidAddonTitles.push(addon?.title);
      }
    });

    if (invalidAddonTitles?.length > 0) {
      toast.error(
        `Please select other available options for: ${invalidAddonTitles?.join(', ')}`
      );
      return false;
    }

    return true;
  };

  return (
    <Drawer open={open} onClose={onClose} anchor="bottom">
      <Box className="productDrawer">
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: -20, right: 8, zIndex: 1, backgroundColor: '#fff', height: '16px', width: '16px' }}
        >
          <CloseOutlined sx={{ fontSize: '12px' }} />
        </IconButton>
        <Grid container>
          <Grid item xs={5} sx={{ justifyContent: 'center' }}>
            <Box className="imageBox">
              <img className="image" src={selectedProduct?.displayImageUrl || selectedProduct?.displayImage} alt="Product" />
            </Box>
          </Grid>
          <Grid item xs={7} sx={{ paddingLeft: '2%' }}>
            <Box className="productDetails">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography className="productName" sx={{ textAlign: 'left' }}>{selectedProduct?.name}</Typography>
              </Box>
              <Typography className="productInfo">{selectedProduct?.description}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <>
                    {selectedProduct?.foodType === 'VEG/VEGAN' ? (
                      <Stack direction={'row'}>
                        <img className='vegNonveg' src={Images.veg} alt='veg' />
                        <img style={{ height: '20px', marginRight: '4px' }} className='vegNonveg' src={Images.vegan} alt='vegan' />
                      </Stack>
                    ) : (
                      <>
                        {selectedProduct && (
                          <img style={{ height: '20px' }} className='vegNonveg' src={selectedProduct.foodType === 'VEG' ? Images.veg : (selectedProduct.foodType === 'VEGAN' ? Images.vegan : Images.nonVeg)} alt={selectedProduct.foodType === 'VEG' ? 'veg' : (selectedProduct.foodType === 'VEGAN' ? 'vegan' : 'non-veg')} />
                        )}
                      </>
                    )}
                  </>
                  <Typography sx={{ color: '#6B6C6E', paddingLeft: '7px' }}>{selectedProduct?.calories} Kcal</Typography>
                </Box>
                <Box sx={{ width: '1.5px', height: '20px', backgroundColor: '#6B6C6E', marginX: '8px' }} />
                <Typography sx={{ color: '#6B6C6E' }}>{selectedProduct?.weight} {selectedProduct?.weightUnit}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        {selectedProduct && (
          <>
            {loadingProductDetails && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loadingProductDetails && selectedProduct?.hasAddon && selectedProduct?.addons && selectedProduct?.addons?.length > 0 && (
              <Box className="addonsBox">
                {selectedProduct?.addons
                  .filter((addon) => !addon?.excludedOutlets?.includes(outletData?._id))
                  .map((addon) =>
                    addon?.values?.some((val) => val?.status !== 'INACTIVE' && !val?.excludedOutlets?.includes(outletData?._id)
                    ) && (
                      <Grid container key={addon._id} sx={{ padding: '10px 10px 7px 10px', backgroundColor: '#fff', marginBottom: '10px', borderRadius: '18px' }}>
                        <Grid item xs={3} sx={{ display: 'flex', paddingTop: '10px', alignItems: addon?.badgeImage ? 'center' : 'normal' }}>
                          {addon?.badgeImage &&
                            <img style={{ width: '40%', paddingRight: '10px' }} src={addon?.badgeImage} />
                          }
                          <Typography className="addonTitle">{addon?.title}</Typography>
                        </Grid>
                        <Grid item xs={9}>
                          {addon?.values?.some(v => v?.selectedImage || v?.nonSelectedImage) ? (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                                {addon?.values
                                  .filter((addon) =>
                                    (!addon?.iconInfo) &&
                                    (addon?.status !== 'INACTIVE' || addon?.isDefault) &&
                                    (!addon?.excludedOutlets?.includes(outletData?._id) || addon?.isDefault)
                                  )
                                  .map((addonValue, sizeIndex, sizeValues) => {
                                    const isSelected = addon?.isMultipleSelect ? Array.isArray(selectedAddons[addon?._id]) && selectedAddons[addon?._id].some((selected) => selected?._id === addonValue?._id) : selectedAddons[addon?._id]?._id === addonValue?._id;
                                    const isDefault = addonValue?.isDefault;

                                    const isExcluded = addonValue?.excludedOutlets?.includes(outletData?._id);
                                    const isDisabled = isExcluded;

                                    const hasExplicitSelection = addon?.isMultipleSelect
                                      ? Array.isArray(selectedAddons[addon?._id]) &&
                                      selectedAddons[addon?._id].length > 0
                                      : !!selectedAddons[addon?._id];

                                    const showSelectedImage = isSelected || (!hasExplicitSelection && addonValue?.isDefault);
                                    const applySizeScale = isSizeAddonGroup(addon, sizeValues);
                                    const sizeTier = applySizeScale ? getAddonSizeTier(addonValue, sizeIndex, sizeValues.length) : null;

                                    return (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: applySizeScale ? 'center' : undefined, minWidth: applySizeScale ? 52 : undefined }}>
                                        <img
                                          key={addonValue?._id}
                                          onClick={() =>
                                            handleAddonSelect(
                                              addon._id,
                                              {
                                                ...addonValue,
                                                offerPrice: addonValue?.price ?? 0,
                                                mrp: addonValue?.price ?? 0,
                                              },
                                              addon?.isMultipleSelect
                                            )
                                          }
                                          src={showSelectedImage ? addonValue?.selectedImage : addonValue?.nonSelectedImage}
                                          alt={addonValue?.value}
                                          className={sizeTier ? `addonSizeIcon size-${sizeTier}` : undefined}
                                          style={{
                                            ...(sizeTier
                                              ? {
                                                  height: ADDON_SIZE_HEIGHTS[sizeTier],
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                                }
                                              : {}),
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            filter: isDisabled ? 'grayscale(100%) opacity(0.6)' : 'none',
                                          }}
                                        />
                                        <Typography className="addonValue" sx={{ color: '#00A4E2' }}>
                                          {addonValue?.subLabel}
                                        </Typography>
                                        <Box>
                                          <Typography color={'green'} variant='caption'>+₹{addonValue?.price ?? 0}</Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                              </Box>
                            </Box>
                          ) : (
                            <Stack spacing={1}>
                              {addon?.values
                                .filter((addon) =>
                                  (!addon?.iconInfo) &&
                                  (addon?.status !== 'INACTIVE' || addon?.isDefault) &&
                                  (!addon?.excludedOutlets?.includes(outletData?._id) || addon?.isDefault)
                                )
                                .map((addonValue) => {

                                  const isSelected = addon?.isMultipleSelect
                                    ? Array.isArray(selectedAddons[addon?._id]) &&
                                    selectedAddons[addon?._id].some(s => s._id === addonValue?._id)
                                    : selectedAddons[addon?._id]?._id === addonValue?._id;

                                  const hasExplicitSelection = addon?.isMultipleSelect
                                    ? Array.isArray(selectedAddons[addon._id]) &&
                                    selectedAddons[addon?._id].length > 0
                                    : Boolean(selectedAddons[addon?._id]);

                                  const isDefault = addonValue?.isDefault;
                                  const showSelected = isSelected || (!hasExplicitSelection && isDefault);

                                  const isExcluded = addonValue?.excludedOutlets?.includes(outletData?._id);
                                  const isInactiveDefault = addonValue?.isDefault && addonValue?.status === 'INACTIVE';
                                  const isDisabled = isExcluded || isInactiveDefault;

                                  return (
                                    <Box
                                      key={addonValue?._id}
                                      className="addonItem"
                                      onClick={() => {
                                        if (isDisabled) {
                                          if (isInactiveDefault) {
                                            toast.error('This default addon is currently unavailable.');
                                          } else if (isExcluded) {
                                            toast.error('This addon is not available at this outlet.');
                                          }
                                          return;
                                        }
                                        handleAddonSelect(addon?._id, {
                                          ...addonValue,
                                          offerPrice: addonValue?.price ?? 0,
                                          mrp: addonValue?.price ?? 0,
                                        }, addon?.isMultipleSelect)
                                      }}
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        padding: '4px 0',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        opacity: isDisabled ? 0.6 : 1,
                                        filter: isDisabled ? 'grayscale(100%) opacity(0.6)' : 'none'
                                      }}
                                    >
                                      <Typography className="addonValue" sx={{ color: '#00A4E2', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                                        {addonValue?.subLabel}
                                      </Typography>
                                      <Box className={`addonSelect ${(isSelected || showSelected) ? 'selected' : 'unselected'}`}>
                                        {addonValue?.value ?? 0} <span style={{ paddingLeft: '7px' }}>+₹{addonValue?.price ?? 0}</span>
                                      </Box>
                                    </Box>
                                  );
                                })}
                            </Stack>
                          )}
                        </Grid>
                      </Grid>
                    )
                  )}
              </Box>
            )}
          </>
        )}
        <Box className="priceAndButton">
          <Box className="addRemoveItem">
            <RemoveCircleIcon className="text" onClick={handleDecreaseQuantity} />
            <Typography sx={{ color: '#000' }} className="text">{quantity}</Typography>
            <AddCircleIcon className="text" onClick={handleIncreaseQuantity} />
          </Box>
          <>
            {outletData?.isEventOutlet === selectedProduct?.isEventProduct ? (
              addingToCart || loadingProductDetails ? (
                <Box className="d-button">
                  <CircularProgress size={18} sx={{ color: '#fff' }} /> {addingToCart ? 'Adding to cart' : 'Loading'}
                </Box>
              ) : (
                <Box className={selectedProduct?.isExhausted ? "exhaustedButton" : "d-button"}
                  onClick={() => {
                    if (selectedProduct?.isExhausted) return;

                    if (!validateAddonsBeforeAddToCart()) {
                      // toast.error('Please select available addon options for all required addon groups.');
                      return;
                    }

                    handleAddToCart();
                  }}
                >
                  {selectedProduct?.isExhausted ? (
                    "Out Of Stock"
                  ) : (
                    <>
                      <span>Add</span>
                      <span className="divider" />
                      <span>₹{(parseFloat(totalPrice) * parseInt(quantity)).toFixed(2)}</span>
                    </>
                  )}
                </Box>
              )
            ) : (
              <Box className="exhaustedButton" sx={{ flex: '0 0 auto', marginLeft: '20px' }}>
                <span style={{ fontSize: '16px' }}>Order this at our store</span>
              </Box>
            )}
          </>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProductDrawer;
